import React, { useMemo, useCallback } from "react";

import classnames from "classnames";

import { FieldError } from "react-hook-form";

import { useWombatTheme } from "../../../theme";

export type SelectProps<T> = {
  /**
   * Label
   */
  label: string;
  className?: string;
  /**
   * Define if the label shoulb only be seen on screen reader
   */
  srOnlyLabel?: boolean;
  /**
   * Disabled state
   * @default false
   */
  disabled?: boolean;
  /**
   * Select id, the component will automatically infer all data-test-id from it.
   *
   * data-test-id strategy:
   *  - Container: input_${id}_group
   *  - Label: input_${id}_label
   *  - Description: input_${id}_description
   *  - Input: input_${id}
   *  - Help text: input_${id}_help_message
   *  - Error message: input_${id}_error_message
   *
   * It will also do the matching from the label to the input in respect to accessibility patterns
   */
  id: string;
  /**
   * Field name
   */
  name?: string;
  /**
   * The react-hook-form error field
   * @default {}
   */
  error?: FieldError;
  /**
   * Options of the select
   */
  items: T[];
  /**
   * Function that give back the value from an item T, used internally to retrieve the current value
   */
  getValue: (item: T) => string;
  /**
   * Default value of the select
   *
   * If the given props is a `T`, the component will call `getValue` on this item
   */
  defaultValue?: T | string;
  /**
   * Placeholder value
   */
  placeholder?: string;
  /**
   * This function is called to render elements inside the select dropdown
   * The content will be wrap inside an `option` HTML tag
   */
  renderItem: (item: T) => React.ReactNode;
  /**
   * This function is triggered each time a change to the value is made
   * @default undefined
   */
  onChange: (item: T) => void;
};

const Select = <T extends unknown>({
  label,
  id,
  name,
  srOnlyLabel,
  disabled = false,
  error,
  getValue,
  defaultValue,
  placeholder,
  className,
  items,
  renderItem,
  onChange,
}: SelectProps<T>) => {
  const { theme } = useWombatTheme();

  const _defaultValue = useMemo((): string | undefined => {
    if (!defaultValue) return undefined;
    if (typeof defaultValue === "string") return defaultValue;

    return getValue(defaultValue);
  }, [defaultValue]);

  const selectStyle = classnames(className || theme.form.select.base, "pr-8");
  const labelStyle = classnames(
    theme.form.select.label[disabled ? "disable" : "able"],
    srOnlyLabel ? "sr-only" : ""
  );
  const optionStyle = classnames(theme.form.select.item.base);

  const handleChange = useCallback(
    (e) => {
      if (onChange) {
        onChange(items.find((item) => getValue(item) === e.target.value));
      }
    },
    [onChange]
  );

  return (
    <label htmlFor={id} className="block">
      <span className={labelStyle}>{label}</span>
      <select
        id={id}
        disabled={disabled}
        className={selectStyle}
        defaultValue={_defaultValue}
        onChange={handleChange}
        name={name}
      >
        {items.map((item, idx) => (
          <option className={optionStyle} value={getValue(item)} key={idx}>
            {renderItem(item)}
          </option>
        ))}
      </select>
    </label>
  );
};

export default React.memo(Select);
